import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { apiRequest } from "../../shared/api/client";
import type { Week39PackagingSnapshot } from "./week39-types";

const lineStaffSchema = z.object({ weekStart: z.iso.date(), date: z.iso.date(), lineName: z.string(), count: z.number().int().min(0),
  version: z.number().int().positive(), updatedAt: z.iso.datetime(), updatedBy: z.string() });
const plannedStaffSchema = z.object({ weekStart: z.iso.date(), date: z.iso.date(), lineName: z.string(),
  count: z.number().int().min(0).nullable(), note: z.string().min(1).optional() });
const responseSchema = z.object({ items: z.array(lineStaffSchema), plannedItems: z.array(plannedStaffSchema), canEdit: z.boolean() });
export type LineStaff = z.infer<typeof lineStaffSchema>;
export type PlannedLineStaff = z.infer<typeof plannedStaffSchema>;

export function useWeek39LineStaff(snapshot: Week39PackagingSnapshot | null) {
  const [items, setItems] = useState<LineStaff[]>([]);
  const [plannedItems, setPlannedItems] = useState<PlannedLineStaff[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const reload = useCallback(async () => {
    if (!snapshot) return;
    try {
      const response = responseSchema.parse(await apiRequest<unknown>(`/api/sarf39/manual-packaging/line-staff?weekStart=${snapshot?.weekStart ?? ""}`, { cache: "no-store" }));
      setItems(response.items.filter((item) => item.weekStart === snapshot.weekStart));
      setPlannedItems(response.plannedItems.filter((item) => item.weekStart === snapshot.weekStart));
      setCanEdit(response.canEdit); setError("");
    } catch (reason) { setCanEdit(false); setError(reason instanceof Error ? reason.message : "Kişi sayıları yüklenemedi."); }
    finally { setLoaded(true); }
  }, [snapshot]);
  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") void reload(); }, 60_000);
    return () => window.clearInterval(timer);
  }, [reload]);
  const save = async (input: { date: string; lineName: string; count: number; expectedVersion: number }) => {
    const saved = lineStaffSchema.parse(await apiRequest<unknown>(`/api/sarf39/manual-packaging/line-staff?weekStart=${snapshot?.weekStart ?? ""}`, {
      method: "PUT", body: JSON.stringify(input),
    }));
    setItems((current) => [...current.filter((item) => !(item.date === saved.date && item.lineName === saved.lineName)), saved]);
    return saved;
  };
  return { items, plannedItems, canEdit, error, loaded, reload, save };
}
